/**
 * POD 门牌免拍上报——GraphQL 类型定义（与 api-server PodExemptTypes.kt 对齐）。
 * 状态/维度用字符串常量而非 enum：后端 schema 即 String，且分类体系纯配置。
 */

export const POD_EXEMPT_SCOPE_TYPE = {
  SINGLE: 'SINGLE',
  FENCE: 'FENCE',
} as const;
export type PodExemptScopeType = (typeof POD_EXEMPT_SCOPE_TYPE)[keyof typeof POD_EXEMPT_SCOPE_TYPE];

export const POD_EXEMPT_STATUS = {
  PENDING_FIRST: 'PENDING_FIRST',
  PENDING_FINAL: 'PENDING_FINAL',
  EFFECTIVE: 'EFFECTIVE',
  REJECTED: 'REJECTED',
  CLOSED: 'CLOSED',
  OFFLINE: 'OFFLINE',
} as const;
export type PodExemptStatus = (typeof POD_EXEMPT_STATUS)[keyof typeof POD_EXEMPT_STATUS];

export const POD_EXEMPT_DICT_TYPE = {
  ADDRESS_TYPE: 'ADDRESS_TYPE',
  REASON: 'REASON',
  REJECT: 'REJECT',
} as const;
export type PodExemptDictType = (typeof POD_EXEMPT_DICT_TYPE)[keyof typeof POD_EXEMPT_DICT_TYPE];

export const POD_EXEMPT_MATERIAL_TYPE = {
  PHOTO: 'PHOTO',
  VIDEO: 'VIDEO',
  FILE: 'FILE',
} as const;
export type PodExemptMaterialType =
  (typeof POD_EXEMPT_MATERIAL_TYPE)[keyof typeof POD_EXEMPT_MATERIAL_TYPE];

export interface PodExemptReportType {
  /** Long 标量到达前端为字符串（回传 mutation 时后端 parseValue 接受 String） */
  id: string;
  reportNo: string;
  scopeType: PodExemptScopeType;
  status: PodExemptStatus;
  /** Long 标量为字符串 */
  dspId: string;
  warehouseCode: string | null;
  addressType: string | null;
  reasonCodes: string[];
  reasonRemark: string | null;
  zipcode: string | null;
  trackingNumber: string | null;
  latestRejectCodes: string | null;
  latestRejectOpinion: string | null;
  dspName: string | null;
  creatorName: string | null;
  /** 仓库显示名（回填），缺失回退 warehouseCode */
  warehouseName: string | null;
  /** 列表展示：单地址=原始地址，围栏=围栏名 */
  displayAddress: string | null;
  /** 围栏覆盖地址数（物化完成前为 null，展示「统计中」） */
  addressCount: number | null;
  /** 明细目标 id（豁免地址 ID），「已通过的地址」页回填；Long 标量到达前端为字符串 */
  targetId: string | null;
  /** 明细生效时间，「已通过的地址」页回填 */
  effectiveAt: string | null;
  /** 近30天豁免单量，「已通过的地址」页回填；Long 标量为字符串，显示前 Number() */
  hit30d: string | null;
  createdAt: string | null;
  /** 材料计数（审核队列链路回填，其它链路为 null） */
  photoCount: number | null;
  videoCount: number | null;
  fileCount: number | null;
  /** 材料不全（按提交端字典规则重算，审核队列链路回填） */
  materialIncomplete: boolean | null;
  /** 近7天单量（数据源未接入，恒为 null 占位） */
  recent7dOrderCount: number | null;
}

/** 审核工作台统计卡（初审=仓管 / 终审=中台，终审不适用的字段为 null） */
export interface PodExemptReviewQueueStatsType {
  /** Long 标量为字符串 */
  pendingCount: string;
  overdueCount: string | null;
  materialIncompleteCount: string | null;
  /** 本周该环节通过率百分比 0~100，本周无审核记录为 null */
  weeklyPassRatePercent: number | null;
}

export interface PodExemptWaybillPreviewType {
  rawAddress: string;
  zipcode: string;
  warehouseCode: string | null;
  /** 同地址已被占用，提交会被防重拦截 */
  alreadyReported: boolean;
  /** 拦截原因：ACTIVE-活跃单占用 / PERMANENT-抽查下线永久拦截；未拦截为 null */
  blockingReason: 'ACTIVE' | 'PERMANENT' | null;
  /** 本 DSP 同地址历史上报条数（含已驳回/已关闭/已下线；他人 DSP 不回显） */
  reportedCount: number;
  latestReportNo: string | null;
  latestStatus: PodExemptStatus | null;
}

export interface PodExemptEffectiveStatsType {
  /** Long 标量为字符串 */
  effectiveCount: string;
  hit30dCount: string;
}

export interface PodExemptAddressType {
  /** Long 标量为字符串 */
  id: string;
  rawAddress: string;
  status: PodExemptStatus;
  zipcode: string | null;
  latitude: number | null;
  longitude: number | null;
  offlineReason: string | null;
  effectiveAt: string | null;
}

export interface PodExemptFenceType {
  /** Long 标量为字符串 */
  id: string;
  name: string | null;
  status: PodExemptStatus;
  zipcode: string | null;
  /** 原始落点序列 JSON（[{latitude, longitude}]），回显重绘用 */
  pointsJson: string | null;
  centerLatitude: number | null;
  centerLongitude: number | null;
  /** 物化完成前为 null，页面展示「统计中」 */
  addressCount: number | null;
  mountStatus: string;
  effectiveAt: string | null;
}

export interface PodExemptMaterialItemType {
  materialType: PodExemptMaterialType;
  objectKey: string;
  category: string | null;
  fileName: string | null;
  durationSec: number | null;
  sortNo: number | null;
  /** 查看用预签名 GET URL（短时效，服务端派生） */
  viewUrl: string | null;
}

export interface PodExemptFlowType {
  action: string;
  operatorRole: string;
  operatorName: string | null;
  opinion: string | null;
  rejectCodes: string[];
  createdAt: string | null;
}

/** 指标计算状态：决定卡片与画廊的展示形态。 */
export type PodExemptMetricsCalcStatus =
  | 'PENDING'
  | 'DONE'
  | 'PARTIAL'
  | 'FAILED'
  | 'UNSTANDARDIZED';

/**
 * 审核参考指标。
 *
 * 空值语义（必须区分「拿不到」与「为零」）：客诉三档为 null 表示客诉数据暂不可用，
 * 应显示「-」而非 0 —— 显示 0 会让审核员误判该地址无客诉，正是最危险的方向。
 */
export interface PodExemptMetricsType {
  calcStatus: PodExemptMetricsCalcStatus;
  /** SELF-本侧自算 / ODS-数仓回流 */
  source: string;
  /** 近30天单量（含取消单） */
  orderCnt30d: number | null;
  orderCnt7d: number | null;
  /** 样本覆盖的不同司机数；≥4 显示「样本充足，可交叉验证」 */
  distinctDriverCnt: number | null;
  /** 样本单的 POD 照片总张数 */
  podPhotoCnt: number | null;
  /** 画廊样本订单 id（Long 标量为字符串）；图片 URL 走懒加载查询 */
  podSampleOrderIds: string[];
  complaintCnt90d: number | null;
  complaintCnt14d: number | null;
  complaintCnt7d: number | null;
  calculatedAt: string | null;
}

/** 画廊一行：一个司机的一单 POD。imageUrls 为 presign 临时地址（10 分钟过期，不缓存）。 */
export interface PodExemptPodSampleType {
  orderId: string;
  driverName: string | null;
  deliveredAt: string | null;
  imageUrls: string[];
}

export interface PodExemptReportDetailType {
  report: PodExemptReportType;
  address: PodExemptAddressType | null;
  fence: PodExemptFenceType | null;
  materials: PodExemptMaterialItemType[];
  flows: PodExemptFlowType[];
  /** 审核参考指标；null 表示尚未开始统计，前端显示「统计中」 */
  metrics: PodExemptMetricsType | null;
}

export interface PodExemptReportPageType {
  content: PodExemptReportType[];
  page: number;
  size: number;
  /** Long 标量为字符串，参与比较/运算前 Number() */
  totalElements: string;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}


export interface PodExemptDictItemType {
  dictType: PodExemptDictType;
  code: string;
  parentCode: string | null;
  /** 后端已按请求语言解析，直接展示 */
  name: string;
  nameZh: string;
  nameEn: string | null;
  nameEs: string | null;
  /** 弹性属性 JSON 原文，用 parseDictExt 解析 */
  extJson: string | null;
  status: number;
  sortNo: number;
}

export interface PodExemptPresignType {
  uploadUrl: string;
  objectKey: string;
  /** epoch millis，Long 标量为字符串 */
  expiresAt: string;
}

export interface PodExemptNearbyItemType {
  targetType: 'ADDRESS' | 'FENCE';
  /** Long 标量为字符串 */
  targetId: string;
  reportId: string;
  display: string | null;
  latitude: number | null;
  longitude: number | null;
  distanceMeters: number;
  /** 明细状态（周边参考含全部状态的历史上报） */
  status: PodExemptStatus;
  /** 上报首选原因 code（字典映射展示） */
  reasonCode: string | null;
}

// ---------- inputs ----------

export interface PodExemptPointInput {
  latitude: number;
  longitude: number;
}

export interface PodExemptMaterialInput {
  objectKey: string;
  category?: string | null;
  sortNo?: number | null;
  durationSec?: number | null;
}

export interface CreatePodExemptReportInput {
  scopeType: PodExemptScopeType;
  /** 提交归属仓（侧边栏当前选中仓）id，必填；Long 标量可传 number/string */
  warehouseId: string | number;
  addressType?: string | null;
  reasonCodes: string[];
  reasonRemark?: string | null;
  trackingNumber?: string | null;
  fenceName?: string | null;
  zipcode?: string | null;
  points?: PodExemptPointInput[] | null;
  materials?: PodExemptMaterialInput[] | null;
}

export interface PodExemptReportFiltersInput {
  statuses?: PodExemptStatus[] | null;
  scopeType?: PodExemptScopeType | null;
  /** ER 单号或运单号，模糊匹配 */
  keyword?: string | null;
  /** 地址类型字典 code */
  addressType?: string | null;
  /** 地址文本 / 围栏名模糊搜 */
  addressKeyword?: string | null;
  /** 审核队列单框搜索：上报ID/运单号/地址/围栏名/DSP名（后端按输入形态分流索引） */
  searchText?: string | null;
  /** DSP 端：当前选中仓 id（后端校验挂靠后按归属仓过滤） */
  warehouseId?: string | number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
}

/**
 * 仓管侧筛选条件：与 DSP 侧分开，warehouseId 必填。
 * 后端 @RequireResourceAndWarehouse 据此做资源码 + 仓权限双重鉴权，缺失会被拒。
 */
export interface WmsPodExemptReportFiltersInput
  extends Omit<PodExemptReportFiltersInput, 'warehouseId'> {
  /** 当前选中仓 id（顶部仓切换） */
  warehouseId: string | number;
}

export interface PodExemptReviewInput {
  /** 取自 report.id（Long 标量字符串，后端 parseValue 接受） */
  reportId: string;
  pass: boolean;
  /** 驳回必填，REJECT 字典 code，最多 3 个 */
  rejectCodes?: string[] | null;
  opinion?: string | null;
}

/** 仓管初审入参：较 QC 终审多一个 warehouseId，用于仓权限校验。 */
export interface WmsPodExemptReviewInput extends PodExemptReviewInput {
  warehouseId: string | number;
}

/**
 * 照片槽位（原因字典 extJson.photoSlots 的一项，见 plan §39）。
 *
 * code 会作为 material.category 落库，审核页按它回查文案 —— 故对应关系不依赖顺序。
 * 三语名各自可空，缺失时按 code 兜底展示。
 */
export interface PodExemptPhotoSlot {
  code?: string;
  nameZh?: string;
  nameEn?: string;
  nameEs?: string;
  required?: boolean;
}

/**
 * 字典 extJson 弹性属性（全部可空，解析失败按无配置处理）。
 *
 * 仅 REASON 使用；ADDRESS_TYPE 的「适用上报维度」存在 parentCode，不进 extJson。
 * 曾经宣称的 tags / photoMin / photoHint / needFinalReview / tip 五个字段实际全无消费方，
 * 2026-09-10 按用户决策一并删除（plan §39 决策 A、B）。
 */
export interface PodExemptDictExt {
  photoSlots?: PodExemptPhotoSlot[];
  requireVideo?: boolean;
}

/**
 * 字典项显示名：**按当前界面语言在前端取**，不依赖后端解析。
 *
 * 后端 `name` 字段是按请求的 `Accept-Language` 头解析的，而浏览器自动带的是系统语言、
 * 与 i18next 切的界面语言无关 —— 用户界面切英文、系统是中文时会拿到中文名，
 * 造成同一页面「界面英文 + 字典中文」。三语字段本来就随查询一起返回，前端自己挑最省事，
 * 也免去改全局 Apollo header 影响其它模块。缺失时回退中文→英文→code，与 photoSlotLabel 同口径。
 */
export function dictDisplayName(
  item: {code: string; nameZh?: string | null; nameEn?: string | null; nameEs?: string | null},
  lang: string | undefined,
): string {
  const normalized = (lang ?? '').toLowerCase();
  const preferred = normalized.startsWith('es')
    ? item.nameEs
    : normalized.startsWith('en')
      ? item.nameEn
      : item.nameZh;
  return preferred?.trim() || item.nameZh?.trim() || item.nameEn?.trim() || item.code;
}

/** 槽位显示名：按当前语言取，缺失回退中文→英文→code。 */
export function photoSlotLabel(slot: PodExemptPhotoSlot, lang: string | undefined): string {
  const normalized = (lang ?? '').toLowerCase();
  const preferred = normalized.startsWith('es')
    ? slot.nameEs
    : normalized.startsWith('en')
      ? slot.nameEn
      : slot.nameZh;
  return preferred?.trim() || slot.nameZh?.trim() || slot.nameEn?.trim() || slot.code || '';
}

export function parseDictExt(extJson: string | null | undefined): PodExemptDictExt {
  if (!extJson) {
    return {};
  }
  try {
    const parsed = JSON.parse(extJson);
    return typeof parsed === 'object' && parsed !== null ? (parsed as PodExemptDictExt) : {};
  } catch {
    return {};
  }
}

/** DSP 抽查记录统计卡（与列表同筛选、同时间窗，口径由服务端保证一致） */
export interface PodExemptDspSpotCheckStatsType {
  /** 抽查批次 = 窗口内出现过的抽查周期数 */
  batchCount: number;
  /** 抽查数 = 窗口内抽查任务数（地址+围栏合计）；Long 标量为字符串 */
  sampleCount: string;
  /** 不通过率 0~1；分母为 0 时为 null，展示「-」而非 0% */
  failRate: number | null;
  /** 触发下线 = 结论为 OFFLINE 的任务数 */
  offlineCount: number;
}

/** DSP 抽查记录一行；未完成的任务 conclusion / assigneeName / doneAt 为空 */
export interface PodExemptSpotTaskForDspType {
  /** Long 标量为字符串 */
  id: string;
  taskNo: string | null;
  periodNo: string | null;
  /** WEEK / BIWEEK / MONTH */
  periodType: string | null;
  /** ADDRESS 单地址 / FENCE 围栏 */
  targetType: string | null;
  targetId: string | null;
  targetNo: string | null;
  targetAddress: string | null;
  targetZipcode: string | null;
  /** 围栏内地址数快照；单地址维度为空，围栏未完成物化时也为空 */
  targetAddressCount: number | null;
  /** PENDING 待抽查 / DONE 已完成 */
  status: string | null;
  /** 实际抽样单数；未抽样时为空 */
  sampleCount: number | null;
  failCount: number | null;
  assigneeName: string | null;
  doneAt: string | null;
  /** MAINTAIN 维持生效 / OFFLINE 不通过下线；未完成为空 */
  conclusion: string | null;
  /** 结论说明 / 驳回理由（监控侧 QA 自由文本，无映射，原样展示） */
  conclusionRemark: string | null;
  createdAt: string | null;
}

export interface PodExemptDspSpotCheckPageType {
  stats: PodExemptDspSpotCheckStatsType;
  content: PodExemptSpotTaskForDspType[];
  page: number;
  size: number;
  /** Long 标量为字符串 */
  totalElements: string;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}
